/**
 * Invitation Codes Management Page (Admin)
 * Allows admins to create, view, and delete invitation codes
 */
import { useState, useEffect } from 'react';
import {
    PlusIcon,
    TrashIcon,
    TicketIcon,
    UserGroupIcon,
    ClipboardDocumentIcon,
    CheckIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { invitations } from '../services/api';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../context/ToastContext';

const EMPLOYEE_TYPES = [
    { value: 'conductor', label: 'Conductor', color: 'cyan' },
    { value: 'inspector', label: 'Inspector', color: 'purple' },
    { value: 'mecanico', label: 'Mecánico', color: 'orange' },
    { value: 'administrativo', label: 'Administrativo', color: 'green' },
];

export default function InvitationCodes() {
    const toast = useToast();
    const [codes, setCodes] = useState([]);
    const [conductores, setConductores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [copiedCode, setCopiedCode] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    // Form state
    const [employeeType, setEmployeeType] = useState('conductor');
    const [selectedConductor, setSelectedConductor] = useState(null);
    const [employeeName, setEmployeeName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [creating, setCreating] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'available', 'used'

    useEffect(() => {
        loadData();
    }, []);


    const loadData = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const [codesRes, conductoresRes] = await Promise.all([
                invitations.getAll(),
                invitations.getConductores()
            ]);
            setCodes(codesRes.codes || []);
            setConductores(conductoresRes.conductores || []);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!employeeName.trim()) {
            toast.error('Introduce un nombre');
            return;
        }
        if (employeeType === 'conductor' && !selectedConductor) {
            toast.error('Selecciona un conductor');
            return;
        }

        setCreating(true);
        try {
            const result = await invitations.create({
                employeeType,
                employeeName: employeeName.trim(),
                conductorId: employeeType === 'conductor' ? selectedConductor?.id : null,
            });

            toast.success(`Código creado: ${result.code}`);
            setShowCreateModal(false);
            resetForm();
            await loadData(true);
        } catch (error) {
            toast.error(error.message || 'Error al crear código');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        const id = deleteConfirm.id;
        if (!id) return;

        try {
            await invitations.delete(id);
            toast.success('Código eliminado');
            await loadData(true);
        } catch (error) {
            toast.error(error.message || 'Error al eliminar');
        }
    };

    const copyToClipboard = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const resetForm = () => {
        setEmployeeType('conductor');
        setSelectedConductor(null);
        setEmployeeName('');
        setSearchQuery('');
    };

    const filteredConductores = conductores.filter(c =>
        c.nombre.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTypeColor = (type) => {
        const found = EMPLOYEE_TYPES.find(t => t.value === type);
        return found?.color || 'gray';
    };

    // Filter codes by status
    const filteredCodes = codes.filter(code => {
        if (statusFilter === 'available') return !code.used_by;
        if (statusFilter === 'used') return code.used_by;
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <TicketIcon className="w-8 h-8 text-cyan-400" />
                        Códigos de Invitación
                    </h1>
                    <p className="text-gray-400 mt-1">
                        Gestiona los códigos para vincular usuarios con empleados
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-medium rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all"
                >
                    <PlusIcon className="w-5 h-5" />
                    Crear Código
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4">
                    <p className="text-gray-400 text-sm">Total Códigos</p>
                    <p className="text-2xl font-bold text-white">{codes.length}</p>
                </div>
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4">
                    <p className="text-gray-400 text-sm">Disponibles</p>
                    <p className="text-2xl font-bold text-green-400">
                        {codes.filter(c => !c.used_by).length}
                    </p>
                </div>
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4">
                    <p className="text-gray-400 text-sm">Utilizados</p>
                    <p className="text-2xl font-bold text-purple-400">
                        {codes.filter(c => c.used_by).length}
                    </p>
                </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
                <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${statusFilter === 'all'
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                        : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/20'
                        }`}
                >
                    Todos ({codes.length})
                </button>
                <button
                    onClick={() => setStatusFilter('available')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${statusFilter === 'available'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/20'
                        }`}
                >
                    Disponibles ({codes.filter(c => !c.used_by).length})
                </button>
                <button
                    onClick={() => setStatusFilter('used')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${statusFilter === 'used'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                        : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/20'
                        }`}
                >
                    Usados ({codes.filter(c => c.used_by).length})
                </button>
            </div>

            {/* Codes List */}
            <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-white/10">
                                <th className="text-left text-gray-400 text-sm font-medium p-4">Código</th>
                                <th className="text-left text-gray-400 text-sm font-medium p-4">Empleado</th>
                                <th className="text-left text-gray-400 text-sm font-medium p-4">Tipo</th>
                                <th className="text-left text-gray-400 text-sm font-medium p-4">Estado</th>
                                <th className="text-left text-gray-400 text-sm font-medium p-4">Usado por</th>
                                <th className="text-right text-gray-400 text-sm font-medium p-4">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCodes.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center text-gray-500 py-12">
                                        {codes.length === 0
                                            ? 'No hay códigos de invitación'
                                            : 'No hay códigos con el filtro seleccionado'}
                                    </td>
                                </tr>
                            ) : (
                                filteredCodes.map(code => (
                                    <tr key={code.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-lg text-cyan-400 tracking-wider">
                                                    {code.code}
                                                </span>
                                                {!code.used_by && (
                                                    <button
                                                        onClick={() => copyToClipboard(code.code)}
                                                        className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                                        title="Copiar código"
                                                    >
                                                        {copiedCode === code.code ? (
                                                            <CheckIcon className="w-4 h-4 text-green-400" />
                                                        ) : (
                                                            <ClipboardDocumentIcon className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-white font-medium">{code.employee_name}</span>
                                            {code.conductor_nombre && (
                                                <span className="text-gray-400 text-sm ml-2">
                                                    ({code.conductor_nombre})
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded-lg text-xs font-medium bg-${getTypeColor(code.employee_type)}-500/20 text-${getTypeColor(code.employee_type)}-400 border border-${getTypeColor(code.employee_type)}-500/30`}>
                                                {code.employee_type}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            {code.used_by ? (
                                                <span className="px-2 py-1 rounded-lg text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">
                                                    Usado
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 rounded-lg text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                                                    Disponible
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-gray-300">
                                            {code.used_by_nombre || '-'}
                                        </td>
                                        <td className="p-4 text-right">
                                            {!code.used_by && (
                                                <button
                                                    onClick={() => handleDelete(code.id)}
                                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                                                    title="Eliminar código"
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => { setShowCreateModal(false); resetForm(); }}
                title="Crear Código de Invitación"
                size="md"
                footer={
                    <>
                        <button
                            onClick={() => { setShowCreateModal(false); resetForm(); }}
                            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleCreate}
                            disabled={creating}
                            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-medium rounded-lg disabled:opacity-50"
                        >
                            {creating ? 'Creando...' : 'Crear Código'}
                        </button>
                    </>
                }
            >
                <div className="space-y-6">
                    {/* Employee Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                            Tipo de Empleado
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            {EMPLOYEE_TYPES.map(type => (
                                <button
                                    key={type.value}
                                    onClick={() => {
                                        setEmployeeType(type.value);
                                        if (type.value !== 'conductor') {
                                            setSelectedConductor(null);
                                        }
                                    }}
                                    className={`p-3 rounded-xl border text-left transition-all ${employeeType === type.value
                                        ? `bg-${type.color}-500/20 border-${type.color}-500/50 text-${type.color}-400`
                                        : 'bg-black/20 border-white/10 text-gray-400 hover:border-white/20'
                                        }`}
                                >
                                    <span className="font-medium">{type.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Conductor Selector (only for conductor type) */}
                    {employeeType === 'conductor' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Seleccionar Conductor
                            </label>
                            <div className="relative mb-3">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Buscar conductor..."
                                    className="w-full pl-10 pr-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                />
                            </div>
                            <div className="max-h-48 overflow-y-auto space-y-1 bg-black/20 rounded-xl p-2">
                                {filteredConductores.length === 0 ? (
                                    <p className="text-gray-500 text-center py-4">No hay conductores</p>
                                ) : (
                                    filteredConductores.map(conductor => (
                                        <button
                                            key={conductor.id}
                                            onClick={() => {
                                                setSelectedConductor(conductor);
                                                setEmployeeName(conductor.nombre);
                                            }}
                                            className={`w-full p-3 rounded-lg text-left flex items-center justify-between transition-all ${selectedConductor?.id === conductor.id
                                                ? 'bg-cyan-500/20 text-cyan-400'
                                                : 'hover:bg-white/5 text-gray-300'
                                                }`}
                                        >
                                            <span>{conductor.nombre}</span>
                                            {conductor.linked_users > 0 && (
                                                <span className="text-xs text-gray-500">
                                                    (ya vinculado)
                                                </span>
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Employee Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Nombre del Empleado
                        </label>
                        <input
                            type="text"
                            value={employeeName}
                            onChange={(e) => setEmployeeName(e.target.value)}
                            placeholder="Nombre que verá el usuario"
                            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        />
                        <p className="text-gray-500 text-xs mt-2">
                            Este nombre aparecerá cuando el empleado use el código
                        </p>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Eliminar Código"
                message="¿Estás seguro de que deseas eliminar este código de invitación?"
                confirmText="Eliminar"
                variant="danger"
            />
        </div>
    );
}
